import { useMutation, useQuery, useQueryClient } from 'react-query';
import queries from '../queries';

const useCompressorEdit = (callbacks = {}) => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ id, data }) => {
      return queries.compressors.edit.query(id, data);
    },
    {
      ...callbacks,
      onSuccess: ({ data }, variables) => {
        queryClient.invalidateQueries(queries.compressors.get.key);
        queryClient.invalidateQueries(queries.companies.list.key);
        queryClient.invalidateQueries(queries.history.companiesHistory.key);
        if (typeof callbacks.onSuccess === 'function') callbacks.onSuccess(data, variables);
      },
    }
  );
};

const useCompressorCreate = (callbacks = {}) => {
  const queryClient = useQueryClient();
  return useMutation(
    (data) => {
      return queries.compressors.create.query(data);
    },
    {
      ...callbacks,
      onSuccess: ({ data }, variables) => {
        // The backend auto-creates a running-state log AND recomputes US-DoE on
        // every compressor create. Invalidate every cache that reflects those.
        queryClient.invalidateQueries(queries.compressors.get.key);
        queryClient.invalidateQueries(queries.compressors.listRunningStateLogs.key);
        queryClient.invalidateQueries(queries.compressors.getCurrentRunningStateLog.key);
        queryClient.invalidateQueries(queries.companies.list.key);
        queryClient.invalidateQueries(queries.history.companiesHistory.key);
        if (typeof callbacks.onSuccess === 'function') callbacks.onSuccess(data, variables);
      },
    }
  );
};

const useCompressorDelete = (callbacks = {}) => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ id }) => {
      return queries.compressors.delete.query(id);
    },
    {
      ...callbacks,
      onSuccess: ({ data }, variables) => {
        // Soft-delete + auto-log + US-DoE recompute on the backend.
        queryClient.invalidateQueries(queries.compressors.get.key);
        queryClient.invalidateQueries(queries.compressors.getDeleted.key);
        queryClient.invalidateQueries(queries.compressors.listRunningStateLogs.key);
        queryClient.invalidateQueries(queries.compressors.getCurrentRunningStateLog.key);
        queryClient.invalidateQueries(queries.companies.list.key);
        queryClient.invalidateQueries(queries.history.companiesHistory.key);
        if (typeof callbacks.onSuccess === 'function') callbacks.onSuccess(data, variables);
      },
    }
  );
};

const useCompressorTemplates = (params = {}, callbacks = {}) => {
  return useQuery([queries.compressors.getTemplates.key, params], () => queries.compressors.getTemplates.query(params), {
    ...callbacks,
    enabled: callbacks.enabled !== false,
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
    keepPreviousData: true,
    select: (data) => {
      const result = queries.compressors.getTemplates.mapper(data);
      return {
        ...result,
        templates: result.templates.map((template) => ({
          ...template,
          label: `${template.compressorModel}, ${template.compressorMake}`,
          value: template.id,
        })),
      };
    },
  });
};

const useCompressorDatasheet = (id, callbacks = {}) => {
  return useQuery([queries.compressors.getDatasheet.key, id], () => queries.compressors.getDatasheet.query(id), {
    ...callbacks,
    enabled: callbacks.enabled !== false && !!id,
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
    select: (data) => {
      return queries.compressors.getDatasheet.mapper(data);
    },
  });
};

const useDeletedCompressors = (companyId, callbacks = {}) => {
  return useQuery(
    [queries.compressors.getDeleted.key, companyId],
    () => queries.compressors.getDeleted.query(companyId),
    {
      ...callbacks,
      enabled: callbacks.enabled !== false && !!companyId,
      select: (data) => queries.compressors.getDeleted.mapper(data),
    }
  );
};

const useCurrentRunningStateLog = (companyId, callbacks = {}) => {
  return useQuery(
    [queries.compressors.getCurrentRunningStateLog.key, companyId],
    () => queries.compressors.getCurrentRunningStateLog.query(companyId),
    {
      ...callbacks,
      enabled: callbacks.enabled !== false && !!companyId,
      select: (data) => queries.compressors.getCurrentRunningStateLog.mapper(data),
    }
  );
};

const useCompressorRunningStateLogs = (companyId, params = {}, callbacks = {}) => {
  return useQuery(
    [queries.compressors.listRunningStateLogs.key, companyId, params],
    () => queries.compressors.listRunningStateLogs.query(companyId, params),
    {
      ...callbacks,
      enabled: callbacks.enabled !== false && !!companyId,
      keepPreviousData: true,
      select: (data) => queries.compressors.listRunningStateLogs.mapper(data),
    }
  );
};

const useCompressorRunningStateLog = (companyId, logId, callbacks = {}) => {
  return useQuery(
    [queries.compressors.getRunningStateLog.key, companyId, logId],
    () => queries.compressors.getRunningStateLog.query(companyId, logId),
    {
      ...callbacks,
      enabled: callbacks.enabled !== false && !!companyId && !!logId,
      staleTime: Infinity,
      cacheTime: 30 * 60 * 1000,
      select: (data) => queries.compressors.getRunningStateLog.mapper(data),
    }
  );
};

const useCreateRunningStateLog = (callbacks = {}) => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ companyId, data }) => queries.compressors.createRunningStateLog.query(companyId, data),
    {
      ...callbacks,
      onSuccess: ({ data }, variables) => {
        queryClient.invalidateQueries(queries.compressors.listRunningStateLogs.key);
        queryClient.invalidateQueries(queries.compressors.getCurrentRunningStateLog.key);
        queryClient.invalidateQueries(queries.compressors.get.key);
        queryClient.invalidateQueries(queries.companies.list.key);
        if (typeof callbacks.onSuccess === 'function') callbacks.onSuccess(data, variables);
      },
    }
  );
};

export default {
  useCompressorCreate,
  useCompressorEdit,
  useCompressorDelete,
  useCompressorTemplates,
  useCompressorDatasheet,
  useDeletedCompressors,
  useCurrentRunningStateLog,
  useCompressorRunningStateLogs,
  useCompressorRunningStateLog,
  useCreateRunningStateLog,
};
